import { useFormik } from 'formik';
import { Platform } from 'react-native';
import {
  ClientPatientSearchParams,
  clientUnauthenticatedSearch,
  getPatients,
  postRequest,
  QueryKeys
} from 'src/api';
import { ImageUpload } from 'src/components/UploadFiles';
import { ApiPracticeInfo, Client, Patient, RefillRequest, RequestType } from 'src/interfaces';
import { isDefined, isPatientActive, queryClient } from 'src/utils';
import { RefillFormData, RefillFormState } from '../model';
import { getInitialValues } from './initialFormData';
import { validationSchema } from './productValidation';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { useMutation } from 'react-query';
import { useCallback, useState } from 'react';
import { State } from 'src/module/FormKit/model';
import { REFILL_STEPS } from './productSteps';
import { INTL_PHONE_MASK, PHONE_MASK, TRACKING_EVENTS } from 'src/constants';
import { isClientMatch } from 'src/scenes/OnlineBooking/BookingProvider/helper';
import mapClientPatientSearch from 'src/scenes/OnlineBooking/BookingProvider/helpers/mapClientPatientSearch';
import { useBoolean, useTrackVisit } from 'src/hooks';
import { getRefillRequestSettings } from './getRefillRequestSettings';
import sentry from 'src/utils/sentry';
import { AxiosError } from 'axios';
import { mask } from 'react-native-mask-text';

interface Args {
  practiceInfo: ApiPracticeInfo;
  user: Client | undefined;
  patient: Patient | undefined;
  requestSettings: ReturnType<typeof getRefillRequestSettings>;
}

const useRefillFormContext = ({
  practiceInfo,
  user,
  patient,
  requestSettings
}: Args): RefillFormState => {
  const { data: visitData, trackEvent } = useTrackVisit({
    initialEventName: TRACKING_EVENTS.APPOINTMENT_REQUEST_PAGE,
    practiceId: practiceInfo.practiceId,
    sourceId: practiceInfo.sourceId,
    clientId: user?.clientId
  });

  const [state, setState] = useState<State<REFILL_STEPS>>({
    current: REFILL_STEPS.CLIENT_INFO,
    visited: { [REFILL_STEPS.CLIENT_INFO]: true }
  });
  const currentStep = state.current;
  const { t } = useTranslation('requests');

  const { mutateAsync: submitProductRequest } = useMutation(
    async (request: RefillRequest) => await postRequest(request),
    {
      onError: (error: Error) => {
        Toast.show({
          type: 'error',
          text1: t('appointmentRequest:errorToast'),
          text2: error.name
        });
      }
    }
  );

  const { value: isSubmitError, toTrue: toError, toFalse: toNoError } = useBoolean(false);
  const formik = useFormik<RefillFormData>({
    initialValues: getInitialValues(user, patient),
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema,
    onSubmit: async (values, { setErrors }) => {
      toNoError();
      const requestImageIds = Object.values(values.additionalInfo.images ?? [])
        .map((imageUpload: ImageUpload) => imageUpload.id)
        .filter(isDefined);
      try {
        await submitProductRequest({
          clientId: values.clientInfo.clientId,
          content: {
            comments: values.additionalInfo.productComments,
            emailAddress: values.clientInfo.emailAddress,
            fullName: values.clientInfo.fullName,
            origin: Platform.OS,
            petName: values.patientInfo.patientId ? undefined : values.patientInfo.name,
            phoneNumber: mask(values.clientInfo?.phoneNumber ?? '', [PHONE_MASK, INTL_PHONE_MASK]),
            preferredContactMethod: values.notificationSettings.preferredContactMethod,
            quantity: values.additionalInfo.productQuantity,
            refill: values.additionalInfo.productDescription,
            smsOptIn: values.notificationSettings.smsOptIn,
            smsOptOut: !values.notificationSettings.smsOptIn
          },
          patientId: values.patientInfo.patientId,
          practiceId: practiceInfo.practiceId,
          requestImageIds,
          sourceId: practiceInfo.sourceId,
          type: RequestType.Refill,
          visitorToken: visitData?.visitorToken,
          visitToken: visitData?.visitToken
        });
      } catch (e) {
        const error = e as AxiosError;
        sentry.addBreadcrumb({
          message: 'Refill request submission failed',
          category: 'error',
          level: 'error',
          data: {
            data: JSON.parse(error.config?.data ?? '{}'),
            url: error.config?.url,
            code: error.code,
            message: error.message
          }
        });
        toError();
      }

      void queryClient.invalidateQueries([QueryKeys.REQUESTS]);
      trackEvent(TRACKING_EVENTS.REFILL_REQUEST_SUBMITTED);
    }
  });
  const { submitForm, values } = formik;

  const advance = useCallback(async () => {
    if (currentStep === REFILL_STEPS.CONFIRMATION) {
      void submitForm();
    }
    setState((prev) => {
      if (prev.current === REFILL_STEPS.STATUS) return prev;
      const next: REFILL_STEPS = prev.current + 1;
      prev.current = next;
      prev.visited[next] = true;
      return { ...prev };
    });
  }, [currentStep, submitForm]);

  const goBack = useCallback(() => {
    setState(({ visited, current }) => ({ visited, current: current - 1 }));
  }, []);

  const goToStep = useCallback((step: REFILL_STEPS) => {
    setState((prev) => {
      prev.current = step;
      prev.visited[step] = true;
      return { ...prev };
    });
  }, []);

  const clientPatientQuery = useMutation({
    mutationFn: async (args: ClientPatientSearchParams) => {
      if (user && isClientMatch({ practiceInfo, clientInfo: values.clientInfo, user })) {
        const patientData = await queryClient.fetchQuery<Patient[]>([QueryKeys.PATIENT], {
          queryFn: getPatients
        });
        const data = mapClientPatientSearch({
          patients: patientData.filter(isPatientActive),
          clientId: user?.clientId
        });
        return data;
      }
      const data = await clientUnauthenticatedSearch(args);
      if (isDefined(data.clientId)) {
        await formik.setFieldValue('clientInfo.clientId', data.clientId);
        trackEvent(TRACKING_EVENTS.REFILL_REQUEST_PAGE_CLIENT_FOUND);
      }

      return mapClientPatientSearch(data);
    }
  });

  return {
    ...formik,
    practiceInfo,
    state,
    setState,
    advance,
    goBack,
    goToStep,
    clientPatientQuery,
    headerText: requestSettings.headerText,
    confirmationText: requestSettings.confirmationText,
    isSubmitError,
    formTitle: t('requests:refillRequest.title'),
    disclaimer: t('requests:refillRequest.disclaimer')
  };
};

export default useRefillFormContext;
